import React, { useRef, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

interface LocationMapProps {
  location: string;
  coordinates: { lat: number; lng: number };
}

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const LocationMap: React.FC<LocationMapProps> = ({ location, coordinates }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Initialize map
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [coordinates.lng, coordinates.lat],
      zoom: 12,
      interactive: false, // Disable interactions for static display
    });

    // Add a circular marker for approximate location (privacy)
    new mapboxgl.Marker({
      color: "#178B8E",
    })
      .setLngLat([coordinates.lng, coordinates.lat])
      .addTo(mapRef.current);

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [coordinates]);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        p: 3,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        mb: 3,
      }}
    >
      {/* Header */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Location
      </Typography>

      {/* Map Container */}
      <Box
        ref={mapContainerRef}
        sx={{
          width: "100%",
          height: 200,
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#E0E0E0",
        }}
      />

      {/* Location Text */}
      <Typography
        variant="body2"
        sx={{ color: "#5A5A5A", mt: 2, textAlign: "center" }}
      >
        Approximate location - {location}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "#999",
          display: "block",
          textAlign: "center",
          mt: 0.5,
        }}
      >
        Exact location provided after booking
      </Typography>
    </Box>
  );
};

export default LocationMap;
